import React, { useEffect, useRef, useState } from "react";
import { Users, UserCheck, ListChecks, ChevronDown, ChevronUp, Search, CheckSquare, Square } from "lucide-react";
import { apiClient } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";

const OPTIONS = [
  { value: "active", label: "All Active", icon: UserCheck },
  { value: "all", label: "All Customers", icon: Users },
  { value: "custom", label: "Select from list", icon: ListChecks },
];

export function AudiencePicker({ audience, setAudience, selectedIds, setSelectedIds }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);
  const draggingRef = useRef(false);
  const dragModeRef = useRef(true);

  useEffect(() => {
    if (audience === "custom" && !loaded) {
      setLoading(true);
      apiClient.get("/customer-updates/audience")
        .then(({ data }) => { setCustomers(data.items); setLoaded(true); })
        .finally(() => setLoading(false));
    }
  }, [audience, loaded]);

  useEffect(() => {
    const stop = () => { draggingRef.current = false; };
    window.addEventListener("mouseup", stop);
    return () => window.removeEventListener("mouseup", stop);
  }, []);

  const sel = new Set(selectedIds);
  const ql = q.trim().toLowerCase();
  const filtered = customers.filter((c) => !ql || (c.name || "").toLowerCase().includes(ql) || (c.phone || "").includes(ql));

  const setOne = (id, on) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (on) n.add(id); else n.delete(id);
    return [...n];
  });
  const toggleOne = (id) => setOne(id, !sel.has(id));
  const selectAll = () => setSelectedIds([...new Set([...selectedIds, ...filtered.map((c) => c.id)])]);
  const deselectAll = () => { const f = new Set(filtered.map((c) => c.id)); setSelectedIds(selectedIds.filter((id) => !f.has(id))); };

  const onRowMouseDown = (id) => {
    draggingRef.current = true;
    dragModeRef.current = !sel.has(id);
    setOne(id, dragModeRef.current);
  };
  const onRowEnter = (id) => { if (draggingRef.current) setOne(id, dragModeRef.current); };

  return (
    <div className="space-y-3" data-testid="audience-picker">
      <div className="grid grid-cols-3 gap-2">
        {OPTIONS.map(({ value, label, icon: Icon }) => {
          const active = audience === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => { setAudience(value); if (value === "custom") setExpanded(true); }}
              className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border bg-surface text-text-muted hover:bg-background"}`}
              data-testid={`audience-option-${value}`}
            >
              <Icon size={16} strokeWidth={1.75} />
              {label}
            </button>
          );
        })}
      </div>

      {audience === "custom" && (
        <div className="rounded-lg border border-border bg-surface" data-testid="audience-custom-panel">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="flex w-full items-center justify-between px-3 py-2.5 text-sm font-medium text-text-main"
            data-testid="audience-custom-toggle"
          >
            <span>{selectedIds.length} customer{selectedIds.length === 1 ? "" : "s"} selected</span>
            {expanded ? <ChevronUp size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}
          </button>

          {expanded && (
            <div className="border-t border-border p-3">
              <div className="mb-2 flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5">
                <Search size={14} strokeWidth={1.5} className="text-text-muted" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name or phone..."
                  className="w-full bg-transparent text-sm outline-none"
                  data-testid="audience-search"
                />
              </div>
              <div className="mb-2 flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={selectAll} data-testid="audience-select-all">
                  <CheckSquare size={14} strokeWidth={1.5} /> Select all
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={deselectAll} data-testid="audience-deselect-all">
                  <Square size={14} strokeWidth={1.5} /> Deselect all
                </Button>
                <span className="ml-auto text-xs text-text-muted">{filtered.length} shown · drag to multi-select</span>
              </div>

              {loading ? (
                <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded-md border border-border" style={{ userSelect: "none" }} data-testid="audience-list">
                  {filtered.map((c) => {
                    const on = sel.has(c.id);
                    return (
                      <div
                        key={c.id}
                        onMouseDown={() => onRowMouseDown(c.id)}
                        onMouseEnter={() => onRowEnter(c.id)}
                        onClick={() => { if (!draggingRef.current) toggleOne(c.id); }}
                        className={`flex cursor-pointer items-center gap-2.5 border-b border-border/60 px-3 py-2 text-sm ${on ? "bg-primary/10" : "hover:bg-background"}`}
                        data-testid={`audience-row-${c.id}`}
                      >
                        {on ? <CheckSquare size={16} strokeWidth={1.75} className="flex-shrink-0 text-primary" /> : <Square size={16} strokeWidth={1.5} className="flex-shrink-0 text-text-muted" />}
                        <span className="min-w-0 flex-1 truncate text-text-main">{c.name}</span>
                        {c.active && <span className="rounded-full bg-success/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-success">Active</span>}
                        <span className="text-xs text-text-muted">{c.phone}</span>
                      </div>
                    );
                  })}
                  {filtered.length === 0 && <p className="px-3 py-6 text-center text-sm text-text-muted">No customers match your search.</p>}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
